import { FC } from 'react'
import { Navigate, RouteObject, useSearchParams } from 'react-router-dom'
import { authStore } from '@/store/authStore'
import { papelPrincipal } from '@/components/layout/menu'
import RenovacoesPage from '@/pages/renovacoes/RenovacoesPage'
import { PorAba } from './a02'
import ReservasCuradoriaPage from '@/pages/curadoria/ReservasCuradoriaPage'
import ValidacaoReservaPage from '@/pages/curadoria/ValidacaoReservaPage'
import ValidacaoReservasListPage from '@/pages/curadoria/ValidacaoReservasListPage'
import DefesaCreditoAprovacaoPage from '@/pages/defesa/DefesaCreditoAprovacaoPage'
import SelecaoParceiroPage from '@/pages/parceiros/SelecaoParceiroPage'
import NovaOportunidadePage from '@/pages/reservas/nova/NovaOportunidadePage'

/**
 * Rotas do A11 (Onda 6) — curadoria, defesa (aprovação) e parceiro no admin.
 *
 * INTEGRAÇÃO (coordenador, no merge): `reservas-curadoria` também está em
 * `rotas/a02.tsx` apontando para a lista antiga (`ReservasListPage` + `?aba=renovacoes`)
 * "até a curadoria (Onda 6)" — que é esta. Como `...rotasA02` vem antes no
 * `router/index.tsx`, a linha do a02 vence; para a página do Figma valer é preciso
 * remover aquela entrada do a02 (a aba `?aba=renovacoes` segue disponível aqui).
 * O placeholder `parceiros` do a02 continua: a seleção de parceiro é POR RESERVA
 * (`reservas/:id/parceiro`), não uma lista — a lista de parceiros é do A12.
 */

/**
 * Fluxo "Registrar reserva" em nome de consultor: na UI só ADMIN (R01 — a escolha do
 * consultor depende de `GET /v1/usuarios`, ADMIN-only no auth). O backend aceita
 * ADMIN/GERENTE (`ehGestor`, 403 aos demais); GERENTE quando `GET /v1/usuarios` abrir
 * para GERENTE (pergunta ao negócio).
 */
const RegistrarReservaEmNomeDe: FC = () => {
  const usuario = authStore((s) => s.usuario)
  const papel = papelPrincipal(usuario?.permissoes)
  const [params] = useSearchParams()
  const consultorId = params.get('consultorId')
  if (papel !== 'ADMIN') return <Navigate to="/reservas-curadoria" replace />
  // Retomar um rascunho (?continuar=) não precisa do consultor: a reserva já existe (10/09).
  if (!consultorId && !params.get('continuar')) return <Navigate to="/reservas-curadoria" replace />
  return (
    <NovaOportunidadePage modoAdmin consultorId={consultorId} consultorNome={params.get('consultorNome')} />
  )
}

const rotasA11: RouteObject[] = [
  // Reservas e Curadoria (back-office). A entrada do a02 saiu no merge; a aba antiga de
  // renovações (`?aba=renovacoes`, alvo do redirect testado de /renovacoes) continua aqui.
  {
    path: 'reservas-curadoria',
    element: <PorAba padrao={<ReservasCuradoriaPage />} abas={{ renovacoes: <RenovacoesPage /> }} />,
  },
  { path: 'reservas-curadoria/registrar', element: <RegistrarReservaEmNomeDe /> },
  // Validação de reservas (06/09) — telas PRÓPRIAS, separadas da curadoria: a lista das
  // reservas aguardando aprovação / em análise e a validação por reserva (dados,
  // diagnóstico, checklist e arquivos; "Iniciar análise" e a decisão). Back-office; as
  // páginas redirecionam CONSULTOR para Minhas Reservas.
  { path: 'validacao-reservas', element: <ValidacaoReservasListPage /> },
  { path: 'validacao-reservas/:id', element: <ValidacaoReservaPage /> },
  // Defesa de Crédito — aprovação sobre o statusDefesa (A08). A redação segue em
  // `reservas/:id/defesa` (router/index.tsx).
  { path: 'reservas/:id/defesa/aprovacao', element: <DefesaCreditoAprovacaoPage /> },
  // Seleção de Parceiro — por reserva, reusando os cards do detalhe.
  { path: 'reservas/:id/parceiro', element: <SelecaoParceiroPage /> },
]

export default rotasA11
